"use strict";
//
// Client side plugin registry.
//
// Responsibilities:
//
// (1) Manages of "dev" plugins.  Dev plugins are remote plugins
// that are visible only on the client side. Remote plugins are
// plugins that are not bundled with the platform; they are instead
// identified by a url to their plugin manifest (i.e. ui-config.json).
//
// (2) Builds and maintains a plugin list & index for lookup of
// plugin metadata (e.g. the set of extension points & contributions).
//
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.PluginRegistry = void 0;
const core_plugin_config_1 = __importDefault(require("./core-plugin.config"));
const contribution_1 = require("../contribution");
class PluginRegistry {
    constructor(config) {
        this.pluginPaths = {};
        this.contributionIndex = null;
        this.config = config;
    }
    /**
     * @return Plugin configs currently loaded by the platform.
     */
    getPluginConfigs() {
        return this.allPluginConfigs;
    }
    /**
     * @return Contribution index
     */
    getContributionIndex() {
        return this.contributionIndex;
    }
    /**
     * @returns array of dev plugin urls from sessionStorage
     */
    getDevPluginUrls() {
        let list = [];
        try {
            list = JSON.parse(sessionStorage.getItem("cisco.dna.core.devPlugins") || "[]");
        }
        catch (e) {
            list = [];
        }
        if (!list.length &&
            this.config.development &&
            this.config.pluginConfigs.length === 0) {
            list.push(`${window.location.protocol}//localhost:3002/ui-config.json`);
        }
        return list;
    }
    /**
     * @param urls
     * @returns Array of dev plugin urls to sessionStorage
     */
    setDevPluginUrls(urls) {
        sessionStorage.setItem("cisco.dna.core.devPlugins", JSON.stringify(urls || []));
        return this.initDevPlugins(true);
    }
    /**
     * Add dev plugin(s) associated with the given plugin manifest url.
     * @param url The plugin manifest url.
     */
    addDevPlugin(url) {
        const urls = this.getDevPluginUrls();
        if (urls.indexOf(url) === -1) {
            urls.push(url);
            return this.setDevPluginUrls(urls);
        }
        else {
            return Promise.resolve();
        }
    }
    /**
     * Remove dev plugin(s) associated with the given plugin manifest url.
     * @param url The plugin manifest url.
     */
    removeDevPlugin(url) {
        const urls = this.getDevPluginUrls();
        const idx = urls.indexOf(url);
        return idx !== -1 ? urls.splice(idx, 1) && this.setDevPluginUrls(urls) : Promise.resolve();
    }
    /**
     * Remove all dev plugins from local storage
     */
    clearDevPlugins() {
        return this.setDevPluginUrls([]);
    }
    /**
     * Transform the given dev config map into a flat list configs,
     * each with a baseUrl based on the configUrl
     *
     * @param configUrl URL from which ui-config.json is been downloaded
     * @param devConfigMap ui-config.json content
     * @returns transformed plugin configs for platform registry
     */
    transformDevConfig(configUrl, devConfigMap) {
        return Object.entries(devConfigMap)
            .map(([pluginId, config]) => {
            if (typeof config !== "object") {
                return null;
            }
            let baseUrl = config.baseUrl;
            if (!baseUrl) {
                const url = new URL(configUrl);
                baseUrl = `${url.protocol}://${url.host}`;
            }
            return {
                ...config,
                pluginId,
                baseUrl,
                __configUrl: configUrl,
                __isDevPlugin: true,
            };
        })
            .filter(Boolean);
    }
    // filter out plugins with duplicate ids from list.
    // plugins that appear earlier in the list win.
    // this allows dev plugins to override non-dev ones.
    dedupe(configs) {
        const existingIds = {};
        return configs.filter(config => {
            if (!existingIds[config.pluginId]) {
                existingIds[config.pluginId] = true;
                return true;
            }
            return false;
        });
    }
    async getDevPluginConfigs() {
        const res = await Promise.all(this.getDevPluginUrls().map(async (url) => {
            try {
                const fetchResult = await fetch(url);
                const uiConfig = await fetchResult.json();
                return this.transformDevConfig(url, uiConfig);
            }
            catch (err) {
                // eslint-disable-next-line
                console.error(`error fetching ${url}:`, err); // NOSONAR
            }
        }));
        return res.filter(Boolean).flat();
    }
    /**
     * Download dev plugin manifests (i.e. ui-config.json) and add them to the index.
     * Returns a Promise that resolves when the process completes.
     * @return Resolves when the process completes.
     */
    async initDevPlugins(force) {
        if (!force && this.allPluginConfigs) {
            return Promise.resolve();
        }
        else {
            const devPluginConfigs = await this.getDevPluginConfigs();
            return this.updateState(devPluginConfigs);
        }
    }
    init(force) {
        return this.initDevPlugins(force);
    }
    //
    // this is the *only* function that updates state
    //
    updateState(devPluginConfigs) {
        const localPluginConfigs = (this.config.pluginConfigs || []).concat([]);
        const devPluginPaths = {};
        devPluginConfigs.forEach(devPlugin => {
            if (devPlugin.pluginId) {
                devPluginPaths[devPlugin.pluginId] = devPlugin.baseUrl;
            }
            this.pluginPaths = { ...this.pluginPaths, ...devPluginPaths };
        });
        this.allPluginConfigs = this.dedupe([
            ...[...localPluginConfigs, ...devPluginConfigs].reverse(),
            core_plugin_config_1.default
        ]);
        this.contributionIndex = new contribution_1.PluginContributionIndex(this.config.locale);
        this.contributionIndex.build(this.allPluginConfigs);
    }
}
exports.PluginRegistry = PluginRegistry;
